import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import PageCard from './components/PageCard';
import SaldoOrcamentario from './components/SaldoOrcamentario';
import PdfConfirmModal from './components/modals/PdfConfirmModal';
import usePdfConfirmModal from './components/hooks/usePdfConfirmModal';
import { API_BASE_URL } from '../config/ApiConfig';
import useDefaultModal from './components/hooks/useDefaultModal';

function SaldoOrcamentarioPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const anoBase = localStorage.getItem('anoBase');

  const { pdfModalData, showPdfModal, closePdfModal, handleConfirm } =
    usePdfConfirmModal();
  const { showDefaultModal } = useDefaultModal();

  // Função para abrir PDF no modal PdfViewer
  const openPdfInViewer = (pdfUrl, title = 'Saldo Orçamentário') => {
    localStorage.setItem('pdfViewerData', JSON.stringify({ url: pdfUrl, title }));
    const event = new CustomEvent('pdfViewerUpdate', { detail: { url: pdfUrl, title } });
    window.dispatchEvent(event);
  };

  // Função utilitária para validar e abrir PDF
  const validateAndOpenPdf = (response, successMessage) => {
    // Verificar status da resposta
    if (response.status !== 200) {
      throw new Error(`Erro no servidor: ${response.status}`);
    }

    // Verificar se há dados na resposta
    if (!response.data || response.data.size === 0) {
      throw new Error('PDF não foi gerado corretamente - arquivo vazio');
    }

    // Verificar se é realmente um PDF (verificação adicional)
    const contentType = response.headers['content-type'];
    if (contentType && !contentType.includes('application/pdf')) {
      console.warn('Tipo de conteúdo inesperado:', contentType);
    }

    // Se chegou até aqui, o PDF está válido
    const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
    const pdfUrl = URL.createObjectURL(pdfBlob);
    openPdfInViewer(pdfUrl, 'Saldo Orçamentário');
    return true;
  };

  const gerarRelatorio = async tipoRelatorio => {
    try {
      const response = await axios.get(
        `${API_BASE_URL.RELATORIO}/saldo-orcamentario`,
        {
          params: {
            cnpjAssociacao: cnpj,
            ano: anoBase,
            tipoRelatorio: tipoRelatorio,
            nomeMunicipio: 'Município',
            nomeEntidade: 'Entidade',
            codigoEntidade: '001',
          },
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      validateAndOpenPdf(response);
    } catch (error) {
      console.error('Erro ao abrir PDF:', error);
      showDefaultModal('error', 'Falha ao gerar o relatório.');
      throw error; // Re-lança para o modal saber que falhou
    }
  };

  // Funções com modal de confirmação
  const handleGerarRelatorioGeral = () => {
    showPdfModal(
      'Relatório de Saldo Orçamentário - Geral',
      'Gerar relatório geral do saldo orçamentário?',
      () => gerarRelatorio('GERAL')
    );
  };

  const handleGerarRelatorioPorPrograma = () => {
    showPdfModal(
      'Relatório de Saldo Orçamentário - Por Programa',
      'Gerar relatório de saldo orçamentário agrupado por programa?',
      () => gerarRelatorio('PROGRAMA')
    );
  };

  const handleGerarRelatorioDetalhado = () => {
    showPdfModal(
      'Relatório de Saldo Orçamentário - Detalhado',
      'Gerar relatório detalhado do saldo orçamentário?',
      () => gerarRelatorio('DETALHADO')
    );
  };

  return (
      <PageCard title="Saldo Orcamentario" icon="bi-pie-chart"
        className="p-3 mb-2 small"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.75)',
          overflowY: 'auto',
          flex: '1 0 auto',
        }}
      >
        <div className="d-flex justify-content-between align-items-center pt-1 pb-3">
          <div>
            <h4 className="text-success text-shadow mb-1">
              SALDO ORÇAMENTÁRIO
            </h4>
            <small className="text-muted">
              Comparativo entre orçamento previsto e despesas pagas
            </small>
          </div>
          <div className="d-flex align-items-center">
            <div className="dropdown me-2">
              <button
                className="btn btn-success btn-sm dropdown-toggle d-inline-flex align-items-center"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="bi bi-file-pdf me-1" />
                Relatórios
              </button>
              <ul className="dropdown-menu">
                <li>
                  <button
                    className="dropdown-item"
                    onClick={handleGerarRelatorioGeral}
                  >
                    <i className="bi bi-file-pdf me-2" />
                    Relatório Geral
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item"
                    onClick={handleGerarRelatorioPorPrograma}
                  >
                    <i className="bi bi-file-pdf me-2" />
                    Por Programa
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item"
                    onClick={handleGerarRelatorioDetalhado}
                  >
                    <i className="bi bi-file-pdf me-2" />
                    Detalhado
                  </button>
                </li>
              </ul>
            </div>
            <Link
              className="btn btn-secondary btn-sm me-2 d-inline-flex align-items-center"
              to="/home"
            >
              <i className="bi bi-house" />
            </Link>
            <Link
              className="btn btn-outline-primary btn-sm me-2 d-inline-flex align-items-center"
              to="/orcamentos"
            >
              <i className="bi bi-file-earmark-text me-1" />
              Orçamentos
            </Link>
            <Link
              className="btn btn-outline-info btn-sm d-inline-flex align-items-center"
              to="/despesas"
            >
              <i className="bi bi-credit-card me-1" />
              Despesas
            </Link>
          </div>
        </div>

        <SaldoOrcamentario cnpj={cnpj} anoBase={anoBase} token={token} />

        <div className="mt-4 p-3 bg-light rounded">
          <h6 className="text-secondary mb-3">
            <i className="bi bi-info-circle me-2"></i>
            Informações sobre o Saldo Orçamentário
          </h6>
          <div className="row">
            <div className="col-md-6">
              <h6 className="text-primary">Conceitos:</h6>
              <ul className="small">
                <li>
                  <strong>Total Orçado:</strong> Soma de todos os valores
                  previstos no orçamento
                </li>
                <li>
                  <strong>Total Pago:</strong> Soma de todas as despesas que
                  foram efetivamente pagas
                </li>
                <li>
                  <strong>Saldo:</strong> Diferença entre o orçado e o pago
                  (Orçado - Pago)
                </li>
                <li>
                  <strong>% Executado:</strong> Percentual do orçamento que foi
                  utilizado
                </li>
              </ul>
            </div>
            <div className="col-md-6">
              <h6 className="text-primary">Status de Execução:</h6>
              <ul className="small">
                <li>
                  <span className="badge bg-success me-1">Baixa execução</span>{' '}
                  ≤ 50% do orçamento
                </li>
                <li>
                  <span className="badge bg-warning me-1">
                    Execução moderada
                  </span>{' '}
                  51% - 80% do orçamento
                </li>
                <li>
                  <span className="badge bg-danger me-1">Alta execução</span>{' '}
                  &gt; 80% do orçamento
                </li>
                <li>
                  <span className="badge bg-danger me-1">
                    Execução excedida
                  </span>{' '}
                  &gt; 100% do orçamento
                </li>
              </ul>
            </div>
          </div>
        </div>
      {/* Modal de Confirmação para PDF */}
      <PdfConfirmModal
        show={pdfModalData.show}
        title={pdfModalData.title}
        message={pdfModalData.message}
        isLoading={pdfModalData.isLoading}
        onConfirm={handleConfirm}
        onClose={closePdfModal}
      />
      </PageCard>
  );
}

export default SaldoOrcamentarioPage;
